// Client-side types for the garden canvas & editor

export interface Point {
  x: number;
  y: number;
}

export interface GardenZone {
  id: string;
  garden_id: string;
  name: string;
  color_pal: number;
  // Legacy rect fields (used for DB storage / bounding box)
  x: number;
  y: number;
  width_cm: number;
  height_cm: number;
  // 4 corner points (cm). When present, the zone is a free quadrilateral.
  // Order: top-left, top-right, bottom-right, bottom-left
  points?: [Point, Point, Point, Point];
}

/**
 * Get the 4 corner points of a zone.
 * If `points` is set, use it. Otherwise derive from the rect fields.
 */
export function getZonePoints(zone: GardenZone): [Point, Point, Point, Point] {
  if (zone.points) return zone.points;
  const { x, y, width_cm: w, height_cm: h } = zone;
  return [
    { x, y },
    { x: x + w, y },
    { x: x + w, y: y + h },
    { x, y: y + h },
  ];
}

/**
 * Compute bounding box from points (for DB compat & display).
 */
export function zoneBoundingBox(points: [Point, Point, Point, Point]) {
  const xs = points.map((p) => p.x);
  const ys = points.map((p) => p.y);
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  return {
    x: minX,
    y: minY,
    width_cm: Math.max(...xs) - minX,
    height_cm: Math.max(...ys) - minY,
  };
}

/**
 * Compute area of a quadrilateral using the shoelace formula (cm²).
 */
export function zoneArea(points: [Point, Point, Point, Point]): number {
  let area = 0;
  for (let i = 0; i < 4; i++) {
    const j = (i + 1) % 4;
    area += points[i].x * points[j].y;
    area -= points[j].x * points[i].y;
  }
  return Math.abs(area) / 2;
}

/**
 * Point-in-polygon test (ray casting) for a convex/concave quadrilateral.
 */
export function pointInZone(px: number, py: number, points: [Point, Point, Point, Point]): boolean {
  let inside = false;
  for (let i = 0, j = 3; i < 4; j = i++) {
    const xi = points[i].x, yi = points[i].y;
    const xj = points[j].x, yj = points[j].y;
    if ((yi > py) !== (yj > py) && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi) {
      inside = !inside;
    }
  }
  return inside;
}

export type PlantStage = "planned" | "sown" | "transplanted" | "harvested";

export interface PlantPlacement {
  id: string;
  zone_id: string;
  plant_id: string;
  x: number;
  y: number;
  variety?: string;
  note?: string;
  sown_at?: string | null; // ISO date (yyyy-mm-dd) or null
  stage?: PlantStage;
}

export interface Garden {
  id: string;
  user_id: string;
  name: string;
  season_year: number;
  orientation: string;
  width_cm: number;
  height_cm: number;
  is_public: boolean;
  zones: GardenZone[];
  placements: PlantPlacement[];
}

// Vertical supports
export type SupportType = "treillis" | "tuteur" | "pergola" | "palette";

export interface VerticalSupport {
  id: string;
  garden_id: string;
  x1: number; // cm
  y1: number;
  x2: number;
  y2: number;
  height_cm: number; // hauteur du support
  type: SupportType;
  name: string;
}

export const SUPPORT_TYPES: { id: SupportType; label: string; emoji: string; defaultHeight: number }[] = [
  { id: "treillis", label: "Treillis", emoji: "\u2593", defaultHeight: 180 },
  { id: "tuteur", label: "Tuteurs", emoji: "\u2502", defaultHeight: 150 },
  { id: "pergola", label: "Pergola", emoji: "\u256c", defaultHeight: 220 },
  { id: "palette", label: "Palette murale", emoji: "\u2591", defaultHeight: 120 },
];

/** Distance from a point to a line segment (cm) */
export function distToSegment(px: number, py: number, x1: number, y1: number, x2: number, y2: number): number {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return Math.hypot(px - x1, py - y1);
  let t = ((px - x1) * dx + (py - y1) * dy) / lenSq;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(px - (x1 + t * dx), py - (y1 + t * dy));
}

// Snap distance for plants to "attach" to a support (cm)
export const SUPPORT_SNAP_CM = 15;

export type CanvasTool = "select" | "zone" | "plant" | "pan" | "eraser" | "support";

export interface CanvasState {
  tool: CanvasTool;
  zoom: number;
  offsetX: number;
  offsetY: number;
  selectedZoneId: string | null;
  selectedPlantId: string | null;
}

// Zone color palette (matches tailwind zone-* colors)
export const ZONE_COLORS = [
  { name: "Vert", hex: "#4a8a3d", bg: "#edf7e8" },
  { name: "Terracotta", hex: "#c4785a", bg: "#fdf0ea" },
  { name: "Lavande", hex: "#6a9bc8", bg: "#eaf2fa" },
  { name: "Moutarde", hex: "#d4a843", bg: "#fdf6e3" },
  { name: "Mauve", hex: "#9b6b9e", bg: "#f5edf5" },
  { name: "Brique", hex: "#c75a5a", bg: "#fdeaea" },
] as const;

// Grid constants
export const GRID_CELL_CM = 25;

// Corner handle hit radius (px at zoom=1)
export const CORNER_HANDLE_RADIUS = 7;
